import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import Breadcrumb from '../../components/shared/Breadcrumb';
import { platformFetch } from '../../platform/apiClient';

const emptyProfile = { email: '', nombre: '', rut: '', telefono_movil: '' };

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CL');
}

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [form, setForm] = useState(emptyProfile);
  const [password, setPassword] = useState({ current_password: '', new_password: '', confirmation: '' });
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({ current: false, new: false, confirmation: false });
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const activeEmployment = useMemo(() => {
    const today = new Date();
    return (profile?.employment || []).filter((item) => !item.vigencia_hasta || new Date(item.vigencia_hasta) >= today);
  }, [profile]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const response = await platformFetch('/api/profile');
      setProfile(response.profile);
      setMfa(response.mfa);
      setForm({
        email: response.profile?.account?.email || '',
        nombre: response.profile?.person?.nombre || '',
        rut: response.profile?.person?.rut || '',
        telefono_movil: response.profile?.contact?.telefono_movil || '',
      });
    } catch (reason) {
      setError(reason.message || 'No fue posible cargar el perfil.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function togglePasswordVisibility(field) {
    setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }));
  }

  async function saveProfile(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await platformFetch('/api/profile', { method: 'PUT', body: JSON.stringify(form) });
      setMessage(response.message || 'Perfil actualizado correctamente.');
      await load();
    } catch (reason) {
      setError(reason.message || 'No se pudo actualizar el perfil.');
    } finally {
      setSaving(false);
    }
  }

  async function savePassword(event) {
    event.preventDefault();
    if (password.new_password !== password.confirmation) {
      setError('La confirmación de la nueva contraseña no coincide.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await platformFetch('/api/profile/password', { method: 'PUT', body: JSON.stringify(password) });
      setMessage(response.message || 'Contraseña actualizada correctamente.');
      setPassword({ current_password: '', new_password: '', confirmation: '' });
      setPasswordOpen(false);
      setPasswordVisibility({ current: false, new: false, confirmation: false });
    } catch (reason) {
      setError(reason.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSaving(false);
    }
  }

  async function startMfa() {
    setSaving(true);
    setError('');
    try {
      const response = await platformFetch('/api/mfa/enroll/start', { method: 'POST' });
      setSetup(response);
      setRecoveryCodes([]);
    } catch (reason) {
      setError(reason.message || 'No se pudo iniciar la configuración MFA.');
    } finally {
      setSaving(false);
    }
  }

  async function verifyMfa(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await platformFetch('/api/mfa/enroll/verify', { method: 'POST', body: JSON.stringify({ code }) });
      setRecoveryCodes(response.recovery_codes || []);
      setSetup(null);
      setCode('');
      setMfa((current) => ({ ...current, enrolled: true }));
    } catch (reason) {
      setError(reason.message || 'No se pudo verificar MFA.');
    } finally {
      setSaving(false);
    }
  }

  async function disableMfa() {
    if (!window.confirm('¿Deshabilitar MFA para esta cuenta?')) return;
    setSaving(true);
    setError('');
    try {
      const response = await platformFetch('/api/mfa/disable', { method: 'POST' });
      setMessage(response.message || 'MFA deshabilitado.');
      setMfa((current) => ({ ...current, enrolled: false }));
      setRecoveryCodes([]);
    } catch (reason) {
      setError(reason.message || 'No se pudo deshabilitar MFA.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="dashboard-main-body"><div className="card p-24">Cargando perfil...</div></div>;

  return (
    <div className="dashboard-main-body">
      <Breadcrumb title="Mi perfil" />
      {error && <div className="alert alert-danger mb-20">{error}</div>}
      {message && <div className="alert alert-success mb-20">{message}</div>}
      <div className="row gy-4">
        <div className="col-xl-4">
          <div className="card h-100"><div className="card-body p-24">
            <div className="w-72-px h-72-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center mb-20"><Icon icon="solar:user-linear" className="text-4xl" /></div>
            <h5 className="mb-4">{profile?.person?.nombre || 'Perfil sin nombre'}</h5>
            <p className="text-secondary-light mb-20">{profile?.account?.email}</p>
            <div className="d-flex flex-column gap-12 text-sm"><div><span className="text-secondary-light">Estado: </span><span className={profile?.account?.estado === 1 ? 'text-success-600 fw-semibold' : 'text-danger-600 fw-semibold'}>{profile?.account?.estado === 1 ? 'Activo' : 'Inactivo'}</span></div><div><span className="text-secondary-light">Cuenta creada: </span>{formatDate(profile?.account?.created_at)}</div>{profile?.person?.rut && <div><span className="text-secondary-light">RUT: </span>{profile.person.rut}</div>}</div>
          </div></div>
        </div>
        <div className="col-xl-8">
          <div className="card"><div className="card-body p-24"><h6 className="mb-20">Datos personales y de contacto</h6><form onSubmit={saveProfile} className="row gy-3">
            <div className="col-md-6"><label className="form-label">Nombre completo</label><input className="form-control" value={form.nombre} onChange={(event) => update('nombre', event.target.value)} required /></div>
            <div className="col-md-6"><label className="form-label">RUT <span className="text-secondary-light">(opcional)</span></label><input className="form-control" placeholder="Ingresa tu RUT" value={form.rut} onChange={(event) => update('rut', event.target.value)} /><small className="text-secondary-light">Ejemplo: 12.345.678-9</small></div>
            <div className="col-md-6"><label className="form-label">Email de acceso</label><input type="email" className="form-control" value={form.email} onChange={(event) => update('email', event.target.value)} required /></div>
            <div className="col-md-6"><label className="form-label">Teléfono móvil</label><input className="form-control" value={form.telefono_movil} onChange={(event) => update('telefono_movil', event.target.value)} /></div>
            <div className="col-12 d-flex justify-content-end"><button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button></div>
          </form></div></div>
        </div>
        {activeEmployment.length > 0 && <div className="col-12"><div className="card"><div className="card-body p-24"><div className="d-flex align-items-center gap-2 mb-20"><Icon icon="solar:buildings-2-linear" className="text-primary-600 text-2xl" /><h6 className="mb-0">Empresa y relación laboral</h6></div><div className="row gy-3">{activeEmployment.map((item) => <div className="col-lg-6" key={item.trabajador_id}><div className="border radius-8 p-16 h-100"><h6 className="mb-8">{item.empresa_fantasia || item.empresa_nombre || 'Empresa sin nombre'}</h6><p className="mb-4 text-secondary-light">Cargo: <strong className="text-primary-light">{item.cargo_nombre || 'Sin cargo'}</strong></p><p className="mb-4 text-secondary-light">Área: <strong className="text-primary-light">{item.area_nombre || 'Sin área'}</strong></p><p className="mb-0 text-secondary-light">Desde: {formatDate(item.vigencia_desde)}</p></div></div>)}</div></div></div></div>}
        <div className="col-12">
          <div className="card">
            <div className="card-body p-24">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <h6 className="mb-4">Contraseña</h6>
                  <p className="text-secondary-light mb-0">Actualiza tu contraseña de acceso cuando lo necesites.</p>
                </div>
                <button type="button" className="btn btn-outline-primary" onClick={() => setPasswordOpen((current) => !current)}>
                  {passwordOpen ? 'Cancelar' : 'Cambiar contraseña'}
                </button>
              </div>
              {passwordOpen && <form onSubmit={savePassword} className="row gy-3 mt-8">
                <div className="col-md-4">
                  <label className="form-label">Contraseña actual</label>
                  <div className="input-group">
                    <input type={passwordVisibility.current ? 'text' : 'password'} className="form-control" value={password.current_password} onChange={(event) => setPassword((current) => ({ ...current, current_password: event.target.value }))} required />
                    <button type="button" className="btn btn-outline-secondary" aria-label="Mostrar contraseña actual" onClick={() => togglePasswordVisibility('current')}><Icon icon={passwordVisibility.current ? 'solar:eye-closed-linear' : 'solar:eye-linear'} /></button>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Nueva contraseña</label>
                  <div className="input-group">
                    <input type={passwordVisibility.new ? 'text' : 'password'} minLength="8" className="form-control" value={password.new_password} onChange={(event) => setPassword((current) => ({ ...current, new_password: event.target.value }))} required />
                    <button type="button" className="btn btn-outline-secondary" aria-label="Mostrar nueva contraseña" onClick={() => togglePasswordVisibility('new')}><Icon icon={passwordVisibility.new ? 'solar:eye-closed-linear' : 'solar:eye-linear'} /></button>
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Confirmar nueva contraseña</label>
                  <div className="input-group">
                    <input type={passwordVisibility.confirmation ? 'text' : 'password'} minLength="8" className="form-control" value={password.confirmation} onChange={(event) => setPassword((current) => ({ ...current, confirmation: event.target.value }))} required />
                    <button type="button" className="btn btn-outline-secondary" aria-label="Mostrar confirmación de contraseña" onClick={() => togglePasswordVisibility('confirmation')}><Icon icon={passwordVisibility.confirmation ? 'solar:eye-closed-linear' : 'solar:eye-linear'} /></button>
                  </div>
                </div>
                <div className="col-12 d-flex justify-content-end"><button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Actualizar contraseña'}</button></div>
              </form>}
            </div>
          </div>
        </div>
        {mfa?.enabled && <div className="col-lg-6"><div className="card h-100"><div className="card-body p-24"><div className="d-flex justify-content-between align-items-start gap-3 mb-20"><div><h6 className="mb-4">MFA de la cuenta</h6><p className="text-secondary-light mb-0">MFA está activo para tu rol{mfa.required ? ' y es obligatorio' : ''}.</p></div><span className={`badge ${mfa.enrolled ? 'bg-success-50 text-success-600' : 'bg-warning-50 text-warning-700'}`}>{mfa.enrolled ? 'Configurado' : 'Pendiente'}</span></div>{!mfa.enrolled && !setup && <button type="button" className="btn btn-primary" onClick={startMfa} disabled={saving}>Configurar autenticador</button>}{setup && <><div className="text-center mb-16"><img src={setup.qr_data_url} width="180" height="180" alt="Código QR de configuración MFA" /></div><p className="text-sm text-secondary-light">Secreto manual: <code>{setup.secret}</code></p><form onSubmit={verifyMfa}><input className="form-control mb-12" inputMode="numeric" pattern="[0-9]{6}" maxLength="6" placeholder="Código de 6 dígitos" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} required /><button className="btn btn-success" disabled={saving}>Verificar MFA</button></form></>}{mfa.enrolled && recoveryCodes.length === 0 && <div><p className="text-success-600 mb-12">MFA está configurado y activo.</p>{!mfa.required && <button type="button" className="btn btn-outline-danger" onClick={disableMfa} disabled={saving}>Deshabilitar MFA</button>}</div>}{recoveryCodes.length > 0 && <div className="alert alert-warning mt-16 mb-0"><strong>Guarda tus códigos de recuperación:</strong><div className="row g-2 mt-8">{recoveryCodes.map((value) => <div className="col-6" key={value}><code>{value}</code></div>)}</div></div>}</div></div></div>}
        {profile?.superadmin && <div className="col-12"><div className="card"><div className="card-body p-24"><div className="d-flex align-items-center gap-2 mb-20"><Icon icon="solar:shield-keyhole-linear" className="text-primary-600 text-2xl" /><h6 className="mb-0">Información de superadmin</h6></div><div className="row gy-3"><div className="col-md-6"><span className="text-secondary-light d-block mb-4">Tenant</span><strong>{profile.superadmin.tenant?.domain || `#${profile.superadmin.tenant?.id || '—'}`}</strong></div><div className="col-md-6"><span className="text-secondary-light d-block mb-4">Estado del tenant</span><strong>{profile.superadmin.tenant?.enabled ? 'Activo' : 'Inactivo'}</strong></div><div className="col-12"><span className="text-secondary-light d-block mb-4">Roles</span><div className="d-flex flex-wrap gap-2">{profile.superadmin.roles.map((role) => <span className="badge bg-primary-50 text-primary-600" key={role.slug}>{role.nombre || role.slug}</span>)}</div></div><div className="col-md-4"><span className="text-secondary-light d-block mb-4">Cuenta / tenant</span><code>{profile.superadmin.technical.account_id} / {profile.superadmin.technical.tenant_id}</code></div><div className="col-md-4"><span className="text-secondary-light d-block mb-4">Nodo</span><code>{profile.superadmin.technical.node_id || '—'}</code></div><div className="col-md-4"><span className="text-secondary-light d-block mb-4">Actualización</span>{formatDate(profile.superadmin.audit.account_updated_at)}</div><div className="col-12"><span className="text-secondary-light d-block mb-8">Últimos eventos MFA</span>{profile.superadmin.audit.mfa_events?.length ? <ul className="mb-0 ps-20">{profile.superadmin.audit.mfa_events.map((event, index) => <li key={`${event.created_at}-${index}`}>{event.event_type} · {formatDate(event.created_at)}</li>)}</ul> : <p className="text-secondary-light mb-0">Sin eventos MFA registrados.</p>}</div></div></div></div></div>}
      </div>
    </div>
  );
}
